"use client";

import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import {
  businessFiltersAtom,
  type BusinessFilters,
} from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { Icon } from "@/components/ui/icon";
import { useAtom } from "jotai";

function PriceDropIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 10 10" fill="none" className={className} aria-hidden="true">
      <path d="M5 2v6M2.5 5.5 5 8l2.5-2.5" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PriceRiseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 10 10" fill="none" className={className} aria-hidden="true">
      <path d="M5 8V2M2.5 4.5 5 2l2.5 2.5" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type PriceDirectionOption = {
  value: NonNullable<BusinessFilters["priceDirection"]>;
  label: string;
  icon: typeof PriceDropIcon;
  color: string;
};

const DIRECTION_OPTIONS: PriceDirectionOption[] = [
  {
    value: "DECREASED",
    label: "Price Dropped",
    icon: PriceDropIcon,
    color: "text-emerald-600",
  },
  {
    value: "INCREASED",
    label: "Price Increased",
    icon: PriceRiseIcon,
    color: "text-amber-600",
  },
];

const WITHIN_DAYS_OPTIONS = [
  { value: null, label: "Any time" },
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const MIN_PERCENT_OPTIONS = [
  { value: null, label: "Any amount" },
  { value: 3, label: "At least 3%" },
  { value: 5, label: "At least 5%" },
  { value: 10, label: "At least 10%" },
  { value: 15, label: "At least 15%" },
  { value: 20, label: "At least 20%" },
];

export function getPriceChangeFilterCount(filters: BusinessFilters) {
  let count = 0;
  if (filters.priceDirection) count++;
  if (filters.priceChangedWithinDays) count++;
  if (filters.minPriceChangePercent) count++;
  return count;
}

export function PriceChangeFilter({ showClear = true }: { showClear?: boolean } = {}) {
  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const { data: facets } = useListingFacetCounts();

  function handleDirectionClick(
    direction: BusinessFilters["priceDirection"],
  ) {
    setFilters((prev) => ({
      ...prev,
      priceDirection: prev.priceDirection === direction ? null : direction,
    }));
  }

  function handleWithinDaysClick(days: number | null) {
    setFilters((prev) => ({
      ...prev,
      priceChangedWithinDays: days,
    }));
  }

  function handleMinPercentClick(percent: number | null) {
    setFilters((prev) => ({
      ...prev,
      minPriceChangePercent: percent,
    }));
  }

  function clearAll() {
    setFilters((prev) => ({
      ...prev,
      priceDirection: null,
      priceChangedWithinDays: null,
      minPriceChangePercent: null,
    }));
  }

  const directionCounts = facets?.priceDirectionCounts;
  const activeCount = getPriceChangeFilterCount(filters);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-medium text-muted-foreground">
          Direction
        </p>
        <div className="flex flex-col gap-1">
          {DIRECTION_OPTIONS.map((option) => {
            const isActive = filters.priceDirection === option.value;
            const OptionIcon = option.icon;
            const count = directionCounts?.[option.value];
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleDirectionClick(option.value)}
                className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-foreground/5 font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <OptionIcon className={`size-3.5 ${option.color}`} />
                <span className="flex-1">{option.label}</span>
                {count != null && (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {count}
                  </span>
                )}
                {isActive && (
                  <Icon icon="CheckSolid" className="size-3.5" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {filters.priceDirection && (
        <>
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              Minimum change
            </p>
            <div className="flex flex-col gap-1">
              {MIN_PERCENT_OPTIONS.map((option) => {
                const isActive =
                  filters.minPriceChangePercent === option.value;
                return (
                  <button
                    key={option.value ?? "any"}
                    type="button"
                    onClick={() => handleMinPercentClick(option.value)}
                    className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-foreground/5 font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {option.label}
                    {isActive && (
                      <Icon icon="CheckSolid" className="ml-auto size-3.5" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              Time period
            </p>
            <div className="flex flex-col gap-1">
              {WITHIN_DAYS_OPTIONS.map((option) => {
                const isActive =
                  filters.priceChangedWithinDays === option.value;
                return (
                  <button
                    key={option.value ?? "any"}
                    type="button"
                    onClick={() => handleWithinDaysClick(option.value)}
                    className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-foreground/5 font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {option.label}
                    {isActive && (
                      <Icon icon="CheckSolid" className="ml-auto size-3.5" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {showClear && activeCount > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="self-start text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          Clear
        </button>
      )}
    </div>
  );
}
